/* Bilingual marketing copy for the public landing page — governance infrastructure for jointly owned assets.
   Authored separately per language (not machine-translated). Keys are stable; only the wording changes between releases.
   Vocabulary follows the Al Mikial RBVA report (Appendix B): Tamreer keeps a *record* of ownership agreed between the owners;
   legal title stays with the deed or company register, which always prevails. No units, prices, wallets, trading or settlement language. */

export const LANDING_COPY = {
  en: {
    skip: "Skip to content",
    meta: {
      title: "Tamreer — Governance and record-keeping for jointly owned assets in Saudi Arabia",
      description: "Shared ownership, clearly recorded. Shares, valuations, decisions and distributions in one audited record — governed by your charter, based on the official government and legal records.",
    },
    nav: { personas: "Who it serves", how: "How it works", screens: "The product", calculator: "Model a holding", pricing: "Pricing", video: "Explainer", trust: "Trust", faq: "FAQ" },
    header: { signIn: "Sign in", create: "Create account", tagline: "Family office · Governance of jointly owned assets" },
    hero: {
      eyebrow: "For Saudi family offices, heirs and partner groups",
      title: "Shared ownership, clearly recorded.",
      subtitle:
        "Shares, valuations, decisions and distributions in one audited record — governed by your charter, based on the official government and legal records.",
      primary: "Create your account",
      secondary: "Model a holding",
      reducers: ["Free for co-owners and valuers", "Fixed fees, published", "Nothing is priced or traded"],
      watch: "Watch the 2-minute explainer",
      frameAlt: "The Tamreer holding page as an owner sees it: the ownership record, the latest valuation, the owner's share and the manager's mandate.",
      frameCaption: "An owner's view of a holding — recorded shares, latest valuation, mandate and decisions. Showcase data.",
      frameBadge: "Showcase holding",
    },
    proof: {
      label: "What you can hold us to",
      items: [
        { k: "record", label: "A record of agreed ownership — never a title registry", detail: "the deed or company register prevails", to: "/legal/terms" },
        { k: "taqeem", label: "Valuations only by Taqeem-accredited valuers", detail: "engaged directly by the owners", href: "#faq" },
        { k: "fees", label: "Fixed fees, published", detail: "never a percentage of value — see pricing", to: "/pricing" },
        { k: "bilingual", label: "Bilingual record, statements and PDFs", detail: "Arabic and English" },
      ],
    },
    problem: {
      eyebrow: "The governance gap",
      title: "Jointly owned assets are common — governing them consistently is hard.",
      body:
        "Ownership sits in deeds and side letters, decisions happen informally, income is reconciled by hand. Tamreer gives co-owners one auditable record that sits alongside the official register — never in its place.",
      stats: [
        { value: "1 asset", label: "many co-owners — shares nobody can quote precisely" },
        { value: "0 policy", label: "for who decides, at what majority, by when" },
        { value: "Quarters", label: "of delay before a lease, a sale or an owner's exit" },
      ],
    },
    personas: {
      eyebrow: "Who it serves",
      title: "Three roles in a governance structure — one agreed record.",
      subtitle: "Co-owners, managers and accredited valuers each see the same record from their own side — with only the actions their role allows.",
      painsLabel: "Today",
      solutionsLabel: "With Tamreer",
      items: [
        {
          id: "co-owners",
          tab: "Co-owners",
          role: "Owner role",
          title: "Co-owners — heirs, family branches & partners",
          tagline: "See your recorded share and its attributable value; receive income by ownership share; exit through notice to your co-owners.",
          pains: [
            "Decision rights differ across branches and partners, but nothing records them.",
            "Disagreements over income and opaque expenses erode trust between co-owners.",
            "No single source of truth for who holds what — or what changed hands and when.",
          ],
          solutions: [
            "Your recorded share mirrors the deed or register — exact, and visible to every co-owner.",
            "Income and shared costs are allocated by ownership share, with a downloadable owner statement.",
            "Propose a transfer to a co-owner, complete it through the official channel, and see it recorded with the registry reference.",
          ],
        },
        {
          id: "pool-managers",
          tab: "Managers",
          role: "Manager role",
          title: "Family offices, estate administrators & managers",
          tagline: "Run holdings like an institution: ownership record, charter rules, valuations and reporting in one place — under a written mandate.",
          pains: [
            "Ownership tables, side agreements and approvals live in spreadsheets and e-mail.",
            "Who agreed what, and which transfers the registry actually confirmed, is hard to keep straight.",
            "Auditors, advisers and branches each ask for a different report.",
          ],
          solutions: [
            "Create a holding, invite co-owners, record each share once — the ownership record stays current.",
            "Charter-weighted proposals with majority rules and deadlines; outcomes written to the audit record.",
            "Accredited valuation rounds, exportable audit record and owner statements — one trail for everyone.",
          ],
        },
        {
          id: "valuators",
          tab: "Valuers",
          role: "Valuer role",
          title: "Accredited Taqeem valuers",
          tagline: "Deliver independent valuations into a record that boards and co-owners cannot dispute.",
          pains: [
            "Reports sent by e-mail are lost, altered or questioned months later.",
            "Chasing managers for deeds, asset briefs and deadlines.",
            "Hard to prove accreditation and track record to institutional clients.",
          ],
          solutions: [
            "Submit Eqaym-referenced valuations and PDF reports into a tamper-evident record.",
            "Assigned rounds, asset briefs and due dates in one queue — nothing slips.",
            "A verified Taqeem licence badge; every finalized valuation carries your name.",
          ],
        },
      ],
    },
    lifecycle: {
      eyebrow: "Governance lifecycle",
      title: "The full life of a jointly owned holding, in eight stages.",
      subtitle: "From the first deed to the latest distribution, every stage lands in the audit record. Title transfers happen through official channels — then they are recorded.",
      steps: [
        { title: "Record the holding", body: "Villas, land, units or company interests — each with its deed or register reference.", chip: "Deeds" },
        { title: "Accredited valuation", body: "Taqeem-licensed valuers submit values and PDF reports per asset.", chip: "Taqeem" },
        { title: "Record each owner's share", body: "Exactly as the deed, inheritance deed or register states it — nothing new is created.", chip: "Register" },
        { title: "Attributable value", body: "Your share × the latest valuation, per asset. For information — not a price.", chip: "Valuation" },
        { title: "Govern by weighted decision", body: "Leases, works and sales decided by ownership share under the charter's rules.", chip: "Majority" },
        { title: "Transfers as records", body: "A proposal to a named co-owner, their answer, the registry's confirmation, then the manager's record. Nothing priced or settled here.", chip: "Record" },
        { title: "Complete through the official channel", body: "Ministry of Justice or company register completes it; Tamreer records the confirmation.", chip: "Registry" },
        { title: "Distribute by ownership share", body: "Rent and profits allocated by share, paid by the owners' bank; Tamreer records the statement.", chip: "Statement" },
      ],
    },
    how: {
      eyebrow: "How it works",
      title: "Operational in four steps.",
      subtitle: "Set the holding up once; from then on every action is recorded under the charter.",
      steps: [
        { title: "Create the holding", body: "Name it, say how it is held (deed or company) and add the assets with their references." },
        { title: "Invite co-owners & record shares", body: "E-mail and legal share for each co-owner (total 100%). They get a bilingual invitation." },
        { title: "Value & confirm the record", body: "An accredited valuer values the assets; you confirm the ownership record." },
        { title: "Operate under the charter", body: "Vote, record distributions, run exit notices — every action lands in the audit record." },
      ],
      cta: "Model a holding",
      lifecycleShow: "See the full lifecycle — eight stages",
      lifecycleHide: "Hide the full lifecycle",
    },
    screens: {
      eyebrow: "The product",
      title: "The same record, from every seat at the table.",
      subtitle: "Real Tamreer screens from a showcase holding — what an owner opens on the phone before a family meeting.",
      note: "Showcase data · names and figures are fictitious",
      items: [
        { k: "holding", tab: "The holding", title: "One page per holding", body: "Recorded shares, the latest accredited valuation, the manager's written mandate and the fixed fee — the facts a co-owner needs before any discussion.", alt: "The holding page: ownership record, latest valuation, the owner's share and the manager's mandate." },
        { k: "decisions", tab: "Decisions", title: "Votes weighted by recorded share", body: "Every proposal shows the majority rule, the deadline, who voted and how each vote was weighted. The outcome is written to the audit record the moment the vote closes.", alt: "The Decisions page: votes due first, then every closed decision with its result and participation." },
        { k: "transactions", tab: "Transactions", title: "Transfers and asset moves as records", body: "A proposal to a named co-owner, their answer, the registry's confirmation and the manager's record — each step visible to the parties, nothing priced or settled on the platform.", alt: "The Transactions hub: share transfers and asset moves across the owner's holdings, with their status." },
      ],
    },
    calculator: {
      eyebrow: "Model a holding",
      title: "See how a shared asset is recorded between its owners.",
      subtitle: "Add the assets, then the co-owners and their shares. See each co-owner's recorded share, attributable value and monthly net — no account needed.",
      pageTitle: "Model a holding — Tamreer",
      back: "Back to the overview",
      valueLabel: "Asset value",
      valueHint: "Total value of the asset(s) in the holding, in Saudi Riyals.",
      presetsLabel: "Quick examples",
      presetsHint: "Tap to add an example asset; tap again to remove it.",
      presets: [
        { id: "villa", label: "Family villa", type: "real_estate", value: 2500000, income: 12000, expense: 1500 },
        { id: "shop", label: "Commercial unit", type: "commercial", value: 900000, income: 6500, expense: 800 },
        { id: "farm", label: "Farm & land", type: "land", value: 6000000, income: 3000, expense: 7500 },
      ],
      assetsLabel: "Assets in the holding",
      assetsHint: "Add every asset the co-owners share — a villa, a shop, farmland, equipment. Each one is recorded as an asset of the holding; their values add up to the holding value and their cashflows to the monthly net.",
      assetNamePlaceholder: "Asset {n}",
      assetType: "Type",
      assetTypes: { real_estate: "Residential", land: "Land", commercial: "Commercial", vehicle: "Vehicle", equipment: "Equipment", business: "Business stake", financial: "Financial", other: "Other" },
      assetValue: "Value",
      addAsset: "Add asset",
      removeAsset: "Remove asset",
      totalValue: "Holding value",
      assetsCount: "{n} assets",
      assetsCountOne: "1 asset",
      needValue: "Give at least one asset a value to see the split.",
      heirsLabel: "Co-owners",
      heirsHint: "Equal shares are assumed here. Switch to Custom to type each co-owner's exact percentage — inheritance shares, family branches, a partner who contributed more, or any agreed structure.",
      modeEqual: "Equal",
      modeCustom: "Custom",
      customHint: "Type each co-owner's percentage. Shares must total 100% — Tamreer records them so the totals always reconcile.",
      heirNamePlaceholder: "Co-owner {n}",
      pctLabel: "Share %",
      addHeir: "Add co-owner",
      removeHeir: "Remove",
      totalShares: "Total",
      adjustTo100: "Adjust to 100%",
      equalize: "Equal shares",
      exampleCustom: "Example: founder branch + 2 branches + family office",
      exampleCustomNames: ["Founder branch", "Branch B", "Branch C", "Family office reserve"],
      underAllocated: "Shares total {total}% — {pct}% ({tokens} units of record) is still unassigned.",
      overAllocated: "Shares total {total}% — more than 100%. Adjust to 100% to continue; the record below is scaled to fit.",
      unallocatedWord: "Unassigned",
      cashflowLabel: "Monthly cashflow",
      cashflowHint: "What the asset earns (rent, profit) and what it costs (operations, maintenance, fees) each month. Every co-owner receives or carries their share of the net.",
      incomeLabel: "Monthly income",
      expenseLabel: "Monthly expenses",
      netLabel: "Net per month",
      netPerToken: "per unit of record",
      incomeWord: "income",
      costsWord: "costs",
      supplyLabel: "Units of record",
      supplyHint: "How finely the record is kept. More units = finer percentages; this is a bookkeeping precision, not a number of instruments.",
      resultsTitle: "Your holding at a glance",
      tokenPrice: "Attributable value per unit (for information)",
      eachHeir: "Each co-owner holds",
      sharesRange: "Share range",
      ownersWord: "co-owners",
      tokensWord: "units of record",
      shareWord: "share",
      netMonthlyWord: "net per month",
      heir: "Co-owner {n}",
      barLabel: "Recorded shares across co-owners",
      smallest: "Finest recorded share: 1 unit = {price} attributable (for information)",
      remainder: "{n} units cannot be split equally — the first {n} co-owners carry one extra unit each (attributable {price}).",
      insightTitle: "What changes with Tamreer",
      insightBefore: "Before: one deed, {heirs} names, income and bills reconciled by hand every month — and one co-owner who wants out can stall everyone.",
      insightAfter: "After: {heirs} owner statements, each showing exactly its recorded share, {net} of net income allocated by share every month, and any co-owner can give notice to the others and complete a transfer through the official channel.",
      insightAfterExpense: "After: {heirs} owner statements, each showing exactly its recorded share, {net} of running costs allocated by share — nobody chases the others for their part — and any co-owner can give notice to the others and complete a transfer through the official channel.",
      insightAfterNone: "After: {heirs} owner statements, each showing exactly its recorded share, every future income or cost allocated by share automatically, and any co-owner can give notice to the others and complete a transfer through the official channel.",
      cta: "Create this holding on Tamreer",
      disclaimer: "Illustrative only. Tamreer records ownership agreed between the owners; legal title is determined by the title deed or company register, which prevails. Real holdings use Taqeem-accredited valuations and the shares set out in the inheritance deed, family charter or partners' agreement — confirm them with qualified counsel.",
      actionsTitle: "Take this scenario with you",
      downloadPdf: "Download PDF",
      downloadPdfHint: "One page, in the language you are viewing",
      shareLink: "Share link",
      shareTitle: "Tamreer holding estimate",
      shareText: "Here is how our shared asset would be recorded between its owners on Tamreer.",
      shareDialogTitle: "Share this scenario",
      shareDialogBody: "Anyone with the link sees the same inputs and results, and can download the PDF in English or Arabic.",
      copyLink: "Copy link",
      linkCopied: "Link copied",
      openLink: "Open",
      saveToPool: "Save as draft holding",
      saveToPoolHint: "For managers: creates a draft holding with these co-owners and shares prefilled.",
      saveToPoolSignIn: "Sign in as a manager to continue — your scenario is saved.",
      pdfReady: "Your PDF is downloading",
      actionError: "Could not save the scenario. Please try again.",
      loadedFromLink: "Scenario loaded from a shared link.",
      loadedFromLinkReset: "Start fresh",
    },
    explainer: {
      eyebrow: "Explainer",
      title: "See Tamreer in two minutes.",
      subtitle: "A two-minute produced video, in English and Arabic. It plays in the language you are browsing in — switch the video language at any time.",
      videoUnavailable: "Your browser could not play this video here — open it in a new tab.",
      openVideo: "Open the video in a new tab",
      play: "Play",
      langLabel: "Video language",
      noVideo: "The explainer video is being prepared and will appear here soon.",
      onlyIn: "The explainer is currently available in {lang} only.",
    },
    trust: {
      eyebrow: "Trust & audit record",
      title: "Built to be audited.",
      subtitle: "Every record can be verified by any co-owner, auditor or regulator — and compared with the official register. Charter rules are applied by the system, not by memory.",
      chips: [
        { k: "charter", label: "Charter-aligned proposals" },
        { k: "ledger", label: "Tamper-evident audit record" },
        { k: "controls", label: "Role-based controls" },
      ],
      cards: [
        { title: "Every decision is tamper-evident", body: "Each allocation, transfer record, vote, notice and distribution is linked to the entry before it. Any later change breaks the chain — and the platform tells you. The whole record exports for your auditor." },
        { title: "Your charter's rules, applied by the system", body: "Majority rules, deadlines and distribution policy are set per holding and applied to every proposal and statement; each vote is weighted by recorded share and written together with its outcome." },
        { title: "A record — never a title, a price or a wallet", body: "Tamreer never holds funds, never sets a price and never transfers title. Completion is recorded only after the registry confirms it; the register and the parties' legal arrangements always prevail." },
        { title: "Statements your auditor can use", body: "Rent, profits and shared costs are allocated by ownership share; owner statements and audit exports are one click away, in English or Arabic." },
      ],
      ledgerTitle: "A live view of the audit record",
      ledgerRows: [
        { kind: "record", tag: "Holding recorded", label: "3 assets · 4 co-owners" },
        { kind: "allocate", tag: "Share allocated", label: "Branch A · 50.00%" },
        { kind: "valuation", tag: "Valuation finalized", label: "Asset values updated" },
        { kind: "distribution", tag: "Rent allocated", label: "By ownership share" },
        { kind: "transfer", tag: "Transfer recorded", label: "Registry reference attached" },
        { kind: "retire", tag: "Share retired", label: "Completion confirmed by the registry" },
      ],
      verified: "Chain integrity verified",
    },
    stats: {
      eyebrow: "Live from the audit record",
      items: [
        { key: "pools_total", label: "Holdings recorded" },
        { key: "assets_total", label: "Assets registered" },
        { key: "ledger_transactions", label: "Audit-record entries" },
        { key: "owners", label: "Co-owners onboarded" },
        { key: "total_nav_sar", label: "Valued assets under governance", currency: true },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      title: "Questions family offices and heirs ask first.",
      items: [
        { q: "Does Tamreer own, hold or transfer anything?", a: "No. Tamreer keeps a record of ownership agreed between the owners and the decisions they take. Legal title is determined by the title deed or the company register, which prevails in case of any difference. Transfers are completed through the Ministry of Justice or the company's official records, and Tamreer records completion only once the registry confirms it." },
        { q: "How does a family charter map to decisions?", a: "Each co-owner's weight is their recorded share. Proposals carry the majority rule and deadline the charter or the law requires — ordinary management by majority of shares, material changes by a higher majority — and every vote is written to the audit record with its weight and outcome." },
        { q: "Who values the assets, and how often?", a: "Only valuers holding a valid Taqeem licence, verified by the platform admin and engaged directly by the owners. Managers open valuation rounds on the cadence the charter sets; each report carries an Eqaym reference and the PDF. Values are shown per asset, and each owner's attributable value is for information only." },
        { q: "What happens when a co-owner wants to transfer their share?", a: "They send a proposal to a named co-owner through Tamreer; that co-owner accepts or declines and the others are informed. Any price is agreed between the parties alone — Tamreer never sets, suggests or settles it. The transfer is completed through the official channel; the manager then records it with the registry reference, the record updates and both parties can flag a dispute if something is wrong." },
        { q: "Does Tamreer handle money?", a: "No. Rent and other income are collected and paid by the owners' bank or a licensed property manager. Tamreer calculates each owner's share, records what was declared and paid, and produces the statements. There are no balances or wallets on the platform." },
        { q: "Is Tamreer a marketplace or an investment product?", a: "No. Nothing is offered to the public, no new money is raised, no outsiders are admitted, and no units are priced or traded. Tamreer is governance infrastructure for assets people already own together." },
        { q: "Is Tamreer available in Arabic?", a: "Yes. The whole platform, including invitations and statements, is available in Arabic with right-to-left layout, and in English." },
        { q: "What technology keeps the record?", a: "An internal hash-chained audit record with full verification. It is a record of rights held elsewhere — not a title register and not a public blockchain." },
      ],
    },
    pricing: {
      pageTitle: "Pricing — Tamreer",
      description: "Fixed, published fees. The organisation that manages the holdings pays; co-owners, heirs and valuers are always free. Never a percentage of value.",
      eyebrow: "Pricing",
      title: "Fixed fees, published. Nothing tied to the value of what you own.",
      subtitle: "The organisation that manages the holdings — a family office, a law firm, an estate administrator — pays a fixed subscription and fixed per-matter fees. Co-owners, heirs and valuers never pay.",
      payerLabel: "Who pays",
      payer: [
        { k: "org", title: "The managing organisation pays", body: "Chooses a plan and pays the fixed fees below. One subscription covers all of its holdings within the plan's limits." },
        { k: "owners", title: "Co-owners and heirs — free", body: "Invited by the manager; they read their record, vote and receive statements at no charge." },
        { k: "valuers", title: "Accredited valuers — free", body: "Engaged and paid by the owners directly. Tamreer takes no referral fee and no share of their work." },
      ],
      plansLabel: "Plans",
      plansTitle: "Three plans. Fixed prices.",
      plansIntro: "Pick the plan by how many holdings and owners your organisation manages. Switch or cancel at the end of the period.",
      billing: { monthly: "per month", yearly: "per year", quotedLabel: "Quoted per family", quotedRange: "typically {from} – {to} per year", exVat: "excl. VAT" },
      choose: "Create your account",
      quote: "Request a quote",
      perMatterLabel: "Fixed per-matter fees",
      perMatterTitle: "One fixed fee per recorded matter.",
      perMatterIntro: "Charged to the managing organisation once per recorded matter, at the moment a manager approves it — always a fixed amount, never a share of the value involved.",
      perMatterCols: { matter: "Matter", fee: "Fee" },
      vat: "All amounts exclude VAT at {vat}%.",
      effective: "Schedule effective from {date}; changes are announced {days} days in advance.",
      indicative: "Indicative schedule — confirmed before launch.",
      notesLabel: "Good to know",
      notes: [
        "Fees are invoiced to the organisation and settled by bank transfer. No payment is taken on the platform and no owner money ever passes through it.",
        "No fee is ever a percentage of an asset's value, of a transfer price or of a distribution.",
        "Every action that creates a fee shows the exact amount first; the manager approves it before the record is written.",
        "The Fee Disclosure is the binding version of this schedule.",
      ],
      faqLabel: "Pricing questions",
      faq: [
        { q: "Why not a percentage of value?", a: "Because Tamreer does not broker, hold or sell anything. A percentage would tie our fee to the value of what you own; fixed fees keep the platform independent of the decisions recorded on it." },
        { q: "What do I pay before the first holding is live?", a: "Nothing. Creating an account, inviting your team and modelling a holding are free. The first fee arises when a holding is activated and a manager approves the onboarding fee." },
        { q: "Can one plan cover several families?", a: "Starter and Professional are for organisations that manage holdings for others and count active holdings and owners across all of them. Family Office is one family's own holdings, quoted on their size." },
      ],
      ctaTitle: "Ready to put a number on it for your family or firm?",
      ctaBody: "Create your account to start, or write to us for a Family Office quote.",
      cta: "Create your account",
      contact: "Contact us",
      disclosure: "Read the full Fee Disclosure",
      loadError: "The live schedule could not be loaded. The Fee Disclosure page carries the published version.",
    },
    closing: {
      title: "Ready to run your shared assets like an institution?",
      body: "Create a holding, invite the co-owners, and let one agreed record keep governance honest.",
      cta: "Create your account",
      secondary: "Model a holding first",
      reducers: ["Free for co-owners and valuers", "Fixed fees, published", "Nothing is priced or traded"],
    },
    footer: {
      note: "Tamreer — governance of jointly owned assets · Riyadh, Saudi Arabia",
      legal: "A record of agreed ownership, not a title registry — the deed or company register prevails.",
      software: "Tamreer is software for records and governance. It does not hold funds, does not price or trade shares, and does not transfer title. Operator details are set out in the Terms of Use.",
      productHeading: "Product",
      legalHeading: "Legal",
      contactHeading: "Contact",
      complaints: "Complaints",
      complaintsEmail: "complaints@tamreer.net",
      complaintsNote: "Acknowledged in 2, answered in 15 business days.",
      languageLabel: "العربية",
      languageHint: "Read this page in Arabic",
      productLinks: [
        { id: "personas", label: "Who it serves", href: "#personas" },
        { id: "how", label: "How it works", href: "#how" },
        { id: "screens", label: "The product", href: "#screens" },
        { id: "calculator", label: "Model a holding", to: "/model" },
        { id: "pricing", label: "Pricing", to: "/pricing" },
        { id: "trust", label: "Trust & audit record", href: "#trust" },
        { id: "faq", label: "FAQ", href: "#faq" },
      ],
      links: [
        { id: "terms", label: "Terms of Use", to: "/legal/terms" },
        { id: "privacy", label: "Privacy Notice", to: "/legal/privacy" },
        { id: "fees", label: "Fees", to: "/legal/fees" },
        { id: "contact", label: "Contact & Complaints", to: "/legal/contact" },
      ],
    },
  },

  ar: {
    skip: "تخطَّ إلى المحتوى",
    meta: {
      title: "تمرير — حوكمة الأصول المشتركة وحفظ سجلاتها في المملكة العربية السعودية",
      description: "ملكية مشتركة، مسجلة بوضوح. الحصص والتقييمات والقرارات والتوزيعات في سجل واحد مدقَّق — يحكمه ميثاقكم، ويستند إلى السجلات الحكومية والقانونية الرسمية.",
    },
    nav: { personas: "لمن هي", how: "كيف تعمل", screens: "المنتج", calculator: "نمذجة وعاء", pricing: "الأسعار", video: "الفيديو التوضيحي", trust: "الثقة", faq: "الأسئلة الشائعة" },
    header: { signIn: "تسجيل الدخول", create: "إنشاء حساب", tagline: "المكاتب العائلية · حوكمة الأصول المشتركة" },
    hero: {
      eyebrow: "للمكاتب العائلية والورثة ومجموعات الشركاء في السعودية",
      title: "ملكية مشتركة، مسجلة بوضوح.",
      subtitle:
        "الحصص والتقييمات والقرارات والتوزيعات في سجل واحد مدقَّق — يحكمه ميثاقكم، ويستند إلى السجلات الحكومية والقانونية الرسمية.",
      primary: "أنشئ حسابك",
      secondary: "نمذجة وعاء",
      reducers: ["مجاني للشركاء والمقيّمين", "رسوم ثابتة ومعلنة", "لا تسعير ولا تداول"],
      watch: "شاهد الفيديو التوضيحي (دقيقتان)",
      frameAlt: "صفحة الوعاء في «تمرير» كما يراها المالك: سجل الملكية وآخر تقييم وحصة المالك وتفويض المدير.",
      frameCaption: "عرض المالك للوعاء — الحصص المسجلة وآخر تقييم والتفويض والقرارات. بيانات عرض توضيحي.",
      frameBadge: "وعاء توضيحي",
    },
    proof: {
      label: "ما يمكنكم محاسبتنا عليه",
      items: [
        { k: "record", label: "سجل لملكية متفق عليها — لا سجل ملكية رسمي أبداً", detail: "الصك أو سجل الشركة هو الذي يسود", to: "/legal/terms" },
        { k: "taqeem", label: "التقييم حصراً عبر مقيّمين معتمدين من «تقييم»", detail: "يتعاقد معهم الملاك مباشرة", href: "#faq" },
        { k: "fees", label: "رسوم ثابتة ومعلنة", detail: "ليست نسبة من القيمة أبداً — اطّلع على الأسعار", to: "/pricing" },
        { k: "bilingual", label: "سجل وكشوف حساب وملفات PDF بلغتين", detail: "العربية والإنجليزية" },
      ],
    },
    problem: {
      eyebrow: "فجوة الحوكمة",
      title: "الأصول المشتركة شائعة — وحوكمتها باتساق أمرٌ صعب.",
      body:
        "الملكية موزّعة بين صكوك ورسائل جانبية، والقرارات غير رسمية، والدخل يُسوَّى يدوياً. تمنح «تمرير» الشركاء سجلاً واحداً قابلاً للتدقيق يقف إلى جانب السجل الرسمي — لا محلّه أبداً.",
      stats: [
        { value: "أصل واحد", label: "شركاء كثيرون — وحصص لا يستطيع أحد تحديدها بدقة" },
        { value: "صفر سياسات", label: "لمن يقرّر، وبأي أغلبية، وقبل أي موعد" },
        { value: "أرباع سنة", label: "من التأخير قبل قرار تأجير أو بيع أو خروج شريك" },
      ],
    },
    personas: {
      eyebrow: "لمن هي",
      title: "ثلاثة أدوار في هيكل الحوكمة — سجل واحد متفق عليه.",
      subtitle: "يرى الشركاء والمديرون والمقيّمون المعتمدون السجل نفسه كلٌّ من جهته — ولا يملك كلٌّ منهم إلا الإجراءات التي يتيحها دوره.",
      painsLabel: "الوضع اليوم",
      solutionsLabel: "مع تمرير",
      items: [
        {
          id: "co-owners",
          tab: "الشركاء",
          role: "دور المالك",
          title: "الشركاء — الورثة وفروع العائلة والشركاء",
          tagline: "اطّلع على حصتك المسجلة وقيمتها المنسوبة، واستلم الدخل حسب نسبة الملكية، واخرج عبر إشعار شركائك.",
          pains: [
            "حقوق القرار تختلف بين الفروع والشركاء، ولا شيء يوثّقها.",
            "الخلافات على الدخل والمصروفات غير الواضحة تُضعف الثقة بين الشركاء.",
            "لا مصدر واحد للحقيقة حول من يملك ماذا — أو ما الذي انتقل ومتى.",
          ],
          solutions: [
            "حصتك المسجلة تطابق الصك أو السجل — دقيقة ومرئية لكل شريك.",
            "يُوزَّع الدخل والتكاليف المشتركة حسب نسبة الملكية، مع كشف حساب للمالك قابل للتنزيل.",
            "اقترح نقلاً إلى شريك، وأتمّه عبر القناة الرسمية، ثم شاهده مقيّداً بمرجع السجل.",
          ],
        },
        {
          id: "pool-managers",
          tab: "المديرون",
          role: "دور المدير",
          title: "المكاتب العائلية ومصفّو التركات والمديرون",
          tagline: "أدِر الأوعية كمؤسسة: سجل الملكية وقواعد الميثاق والتقييمات والتقارير في مكان واحد — بموجب تفويض مكتوب.",
          pains: [
            "جداول الملكية والاتفاقات الجانبية والموافقات مبعثرة في جداول بيانات ورسائل بريد.",
            "يصعب تتبّع من وافق على ماذا، وأي نقلٍ أكّده السجل الرسمي فعلاً.",
            "المدققون والمستشارون والفروع يطلب كل منهم تقريراً مختلفاً.",
          ],
          solutions: [
            "أنشئ وعاءً، وادعُ الشركاء، وقيّد كل حصة مرة واحدة — ويبقى سجل الملكية محدّثاً.",
            "مقترحات مرجّحة وفق الميثاق مع قواعد أغلبية ومواعيد نهائية؛ والنتائج تُكتب في سجل التدقيق.",
            "جولات تقييم معتمدة، وتصدير لسجل التدقيق وكشوف حساب الملاك — مسار واحد للجميع.",
          ],
        },
        {
          id: "valuators",
          tab: "المقيّمون",
          role: "دور المقيّم",
          title: "المقيّمون المعتمدون من «تقييم»",
          tagline: "قدّم تقييمات مستقلة في سجل لا تستطيع المجالس ولا الشركاء الطعن فيه.",
          pains: [
            "التقارير المرسلة بالبريد تُفقد أو تُعدَّل أو يُشكَّك فيها بعد أشهر.",
            "ملاحقة المديرين للحصول على الصكوك وملخصات الأصول والمواعيد.",
            "صعوبة إثبات الاعتماد وسجل الأعمال أمام العملاء المؤسسيين.",
          ],
          solutions: [
            "قدّم تقييمات بمراجع «قيّم» وتقارير PDF في سجل لا يمكن التلاعب به.",
            "الجولات المكلَّف بها وملخصات الأصول والمواعيد في قائمة واحدة — لا شيء يُنسى.",
            "شارة رخصة «تقييم» موثّقة؛ وكل تقييم مُعتمد يحمل اسمك.",
          ],
        },
      ],
    },
    lifecycle: {
      eyebrow: "دورة الحوكمة",
      title: "دورة حياة الوعاء المشترك كاملةً في ثماني مراحل.",
      subtitle: "من أول صك إلى آخر توزيع، كل مرحلة في سجل التدقيق. ونقل الملكية يُتمّ عبر القنوات الرسمية — ثم يُقيَّد.",
      steps: [
        { title: "قيد الوعاء", body: "فلل أو أراضٍ أو وحدات أو حصص في شركات — كلٌّ بمرجع صكّه أو سجله.", chip: "صكوك" },
        { title: "تقييم معتمد", body: "يقدّم مقيّمون مرخّصون من «تقييم» القيم وتقارير PDF لكل أصل.", chip: "تقييم" },
        { title: "قيد حصة كل مالك", body: "تماماً كما ينصّ عليها الصك أو صك حصر الورثة أو السجل — لا يُنشأ شيء جديد.", chip: "السجل" },
        { title: "القيمة المنسوبة", body: "حصتك × آخر تقييم، لكل أصل. للاطلاع — لا سعر.", chip: "التقييم" },
        { title: "الحوكمة بالقرار المرجّح", body: "التأجير والأعمال والبيع تُقرَّر بنسبة الملكية وفق قواعد الميثاق.", chip: "الأغلبية" },
        { title: "النقل كقيود", body: "مقترح إلى شريك محدد، ثم ردّه، ثم تأكيد السجل الرسمي، ثم قيد المدير. لا تسعير ولا تسوية هنا.", chip: "قيد" },
        { title: "الإتمام عبر القناة الرسمية", body: "تُتمّه وزارة العدل أو سجل الشركة؛ وتقيّد «تمرير» التأكيد.", chip: "السجل الرسمي" },
        { title: "التوزيع حسب نسبة الملكية", body: "إيجارات وأرباح حسب الحصة يدفعها بنك الملاك؛ وتقيّد «تمرير» كشف الحساب.", chip: "كشف حساب" },
      ],
    },
    how: {
      eyebrow: "كيف تعمل",
      title: "جاهزة للتشغيل في أربع خطوات.",
      subtitle: "يُجهَّز الوعاء مرة واحدة؛ ومن بعدها يُقيَّد كل إجراء وفق الميثاق.",
      steps: [
        { title: "أنشئ الوعاء", body: "سمِّه، وحدّد كيف يُمسك (صك أو شركة)، وأضف الأصول بمراجعها." },
        { title: "ادعُ الشركاء وقيّد الحصص", body: "بريد كل شريك وحصته القانونية (المجموع 100%). تصلهم دعوة ثنائية اللغة." },
        { title: "قيّم وأكّد السجل", body: "يقيّم مقيّم معتمد الأصول؛ وتؤكد أنت سجل الملكية." },
        { title: "شغّل وفق الميثاق", body: "صوّت، وقيّد التوزيعات، وأدِر إشعارات الخروج — كل إجراء في سجل التدقيق." },
      ],
      cta: "نمذجة وعاء",
      lifecycleShow: "اعرض دورة الحوكمة كاملة — ثماني مراحل",
      lifecycleHide: "أخفِ دورة الحوكمة الكاملة",
    },
    screens: {
      eyebrow: "المنتج",
      title: "السجل نفسه، من كل مقعد على الطاولة.",
      subtitle: "شاشات حقيقية من «تمرير» لوعاء توضيحي — ما يفتحه المالك على هاتفه قبل اجتماع العائلة.",
      note: "بيانات عرض توضيحي · الأسماء والأرقام افتراضية",
      items: [
        { k: "holding", tab: "الوعاء", title: "صفحة واحدة لكل وعاء", body: "الحصص المسجلة وآخر تقييم معتمد وتفويض المدير المكتوب والرسوم الثابتة — الحقائق التي يحتاجها الشريك قبل أي نقاش.", alt: "صفحة الوعاء: سجل الملكية وآخر تقييم وحصة المالك وتفويض المدير." },
        { k: "decisions", tab: "القرارات", title: "أصوات مرجّحة بالحصة المسجلة", body: "يعرض كل مقترح قاعدة الأغلبية والموعد النهائي ومن صوّت وكيف رُجِّح كل صوت. وتُكتب النتيجة في سجل التدقيق لحظة إغلاق التصويت.", alt: "صفحة القرارات: الأصوات المنتظرة أولاً، ثم كل قرار مغلق بنتيجته ونسبة المشاركة." },
        { k: "transactions", tab: "المعاملات", title: "النقل وتحريك الأصول كقيود", body: "مقترح إلى شريك محدد، ثم ردّه، ثم تأكيد السجل الرسمي، ثم قيد المدير — كل خطوة مرئية للأطراف، ولا تسعير ولا تسوية على المنصة.", alt: "مركز المعاملات: نقل الحصص وتحريك الأصول عبر أوعية المالك مع حالة كل منها." },
      ],
    },
    calculator: {
      eyebrow: "نمذجة وعاء",
      title: "شاهد كيف يُقيَّد الأصل المشترك بين ملاكه.",
      subtitle: "أضف الأصول، ثم الشركاء وحصصهم. شاهد حصة كل شريك المسجلة وقيمتها المنسوبة وصافيه الشهري — دون حساب.",
      pageTitle: "نمذجة وعاء — تمرير",
      back: "العودة إلى الصفحة الرئيسية",
      valueLabel: "قيمة الأصل",
      valueHint: "القيمة الإجمالية للأصل أو الأصول في الوعاء، بالريال السعودي.",
      presetsLabel: "أمثلة سريعة",
      presetsHint: "اضغط لإضافة أصل كمثال؛ واضغط مجدداً لإزالته.",
      presets: [
        { id: "villa", label: "فيلا العائلة", type: "real_estate", value: 2500000, income: 12000, expense: 1500 },
        { id: "shop", label: "وحدة تجارية", type: "commercial", value: 900000, income: 6500, expense: 800 },
        { id: "farm", label: "مزرعة وأرض", type: "land", value: 6000000, income: 3000, expense: 7500 },
      ],
      assetsLabel: "الأصول في الوعاء",
      assetsHint: "أضف كل أصل يشترك فيه الملاك — فيلا، محل، أرض زراعية، معدات. يُقيَّد كل منها أصلاً في الوعاء؛ تُجمع قيمها في قيمة الوعاء وتدفقاتها في الصافي الشهري.",
      assetNamePlaceholder: "الأصل {n}",
      assetType: "النوع",
      assetTypes: { real_estate: "عقار سكني", land: "أرض", commercial: "عقار تجاري", vehicle: "مركبة", equipment: "معدات", business: "حصة في منشأة", financial: "أداة مالية", other: "أخرى" },
      assetValue: "القيمة",
      addAsset: "إضافة أصل",
      removeAsset: "إزالة الأصل",
      totalValue: "قيمة الوعاء",
      assetsCount: "{n} أصول",
      assetsCountOne: "أصل واحد",
      needValue: "أدخل قيمة لأصل واحد على الأقل لعرض التقسيم.",
      heirsLabel: "الشركاء",
      heirsHint: "نفترض هنا حصصاً متساوية. اختر «مخصّص» لإدخال النسبة الدقيقة لكل شريك — أنصبة الإرث، أو فروع العائلة، أو شريك ساهم بأكثر، أو أي هيكل متفق عليه.",
      modeEqual: "متساوية",
      modeCustom: "مخصّص",
      customHint: "أدخل نسبة كل شريك. يجب أن يكون مجموع الحصص 100% — وتقيّدها تمرير بحيث تتطابق المجاميع دائماً.",
      heirNamePlaceholder: "الشريك {n}",
      pctLabel: "الحصة %",
      addHeir: "إضافة شريك",
      removeHeir: "إزالة",
      totalShares: "المجموع",
      adjustTo100: "ضبط إلى 100%",
      equalize: "حصص متساوية",
      exampleCustom: "مثال: فرع المؤسس + فرعان + المكتب العائلي",
      exampleCustomNames: ["فرع المؤسس", "الفرع ب", "الفرع ج", "احتياطي المكتب العائلي"],
      underAllocated: "مجموع الحصص {total}% — ما زالت {pct}% ({tokens} وحدة قيد) غير موزّعة.",
      overAllocated: "مجموع الحصص {total}% — أكثر من 100%. اضبط المجموع إلى 100% للمتابعة؛ القيد أدناه مُقلَّص ليتطابق.",
      unallocatedWord: "غير موزّع",
      cashflowLabel: "التدفق النقدي الشهري",
      cashflowHint: "ما يدرّه الأصل (إيجار، ربح) وما يكلّفه (تشغيل، صيانة، رسوم) كل شهر. يحصل كل شريك على نصيبه من الصافي أو يتحمّله.",
      incomeLabel: "الدخل الشهري",
      expenseLabel: "المصروفات الشهرية",
      netLabel: "الصافي شهرياً",
      netPerToken: "لكل وحدة قيد",
      incomeWord: "دخل",
      costsWord: "تكاليف",
      supplyLabel: "وحدات القيد",
      supplyHint: "مدى دقة مسك السجل. وحدات أكثر = نسب أدق؛ وهي دقة محاسبية لا عدد أدوات.",
      resultsTitle: "وعاؤك في لمحة",
      tokenPrice: "القيمة المنسوبة لكل وحدة (للاطلاع)",
      eachHeir: "يملك كل شريك",
      sharesRange: "نطاق الحصص",
      ownersWord: "شركاء",
      tokensWord: "وحدة قيد",
      shareWord: "الحصة",
      netMonthlyWord: "صافي شهرياً",
      heir: "الشريك {n}",
      barLabel: "الحصص المسجلة بين الشركاء",
      smallest: "أدق حصة مسجلة: وحدة واحدة = {price} قيمة منسوبة (للاطلاع)",
      remainder: "لا يمكن تقسيم {n} وحدة بالتساوي — يحمل أول {n} شركاء وحدة إضافية لكل منهم (قيمتها المنسوبة {price}).",
      insightTitle: "ما الذي يتغيّر مع تمرير",
      insightBefore: "قبل: صك واحد، {heirs} أسماء، ودخل وفواتير تُسوَّى يدوياً كل شهر — وشريك واحد يريد الخروج قد يعطّل الجميع.",
      insightAfter: "بعد: {heirs} كشوف حساب للملاك يُظهر كلٌّ منها حصته المسجلة بدقة، وصافي دخل {net} يُوزَّع حسب الحصة كل شهر، ويمكن لأي شريك إشعار الآخرين وإتمام النقل عبر القناة الرسمية.",
      insightAfterExpense: "بعد: {heirs} كشوف حساب للملاك يُظهر كلٌّ منها حصته المسجلة بدقة، وتكاليف تشغيل بواقع {net} تُوزَّع حسب الحصة — فلا يطالب أحد الآخرين بنصيبهم — ويمكن لأي شريك إشعار الآخرين وإتمام النقل عبر القناة الرسمية.",
      insightAfterNone: "بعد: {heirs} كشوف حساب للملاك يُظهر كلٌّ منها حصته المسجلة بدقة، وكل دخل أو تكلفة مستقبلية تُوزَّع حسب الحصة تلقائياً، ويمكن لأي شريك إشعار الآخرين وإتمام النقل عبر القناة الرسمية.",
      cta: "أنشئ هذا الوعاء على تمرير",
      disclaimer: "لأغراض التوضيح فقط. تقيّد «تمرير» الملكية المتفق عليها بين الملاك؛ أما الملكية القانونية فيحددها الصك أو سجل الشركة وهو الذي يسود. تعتمد الأوعية الفعلية على تقييمات معتمدة من «تقييم» والحصص المحددة في صك حصر الورثة أو ميثاق العائلة أو اتفاقية الشركاء — ويُنصح بتأكيدها مع مستشار مختص.",
      actionsTitle: "خذ هذا السيناريو معك",
      downloadPdf: "تنزيل PDF",
      downloadPdfHint: "صفحة واحدة، باللغة التي تعرضها الآن",
      shareLink: "مشاركة الرابط",
      shareTitle: "تقدير وعاء تمرير",
      shareText: "هكذا سيُقيَّد أصلنا المشترك بين ملاكه على تمرير.",
      shareDialogTitle: "مشاركة هذا السيناريو",
      shareDialogBody: "يرى كل من لديه الرابط نفس المدخلات والنتائج، ويمكنه تنزيل ملف PDF بالعربية أو الإنجليزية.",
      copyLink: "نسخ الرابط",
      linkCopied: "تم نسخ الرابط",
      openLink: "فتح",
      saveToPool: "حفظ كوعاء مسودة",
      saveToPoolHint: "للمديرين: يُنشئ وعاء مسودة بهؤلاء الشركاء وحصصهم مسبقاً.",
      saveToPoolSignIn: "سجّل الدخول كمدير للمتابعة — تم حفظ السيناريو.",
      pdfReady: "يتم تنزيل ملف PDF",
      actionError: "تعذّر حفظ السيناريو. حاول مرة أخرى.",
      loadedFromLink: "تم تحميل السيناريو من رابط مشترك.",
      loadedFromLinkReset: "ابدأ من جديد",
    },
    explainer: {
      eyebrow: "الفيديو التوضيحي",
      title: "شاهد تمرير في دقيقتين.",
      subtitle: "فيديو مُنتَج مدته دقيقتان، بالعربية والإنجليزية. يُعرض بلغة الموقع التي تتصفح بها — ويمكنك تبديل لغة الفيديو في أي وقت.",
      videoUnavailable: "لم يتمكن متصفحك من تشغيل هذا الفيديو هنا — افتحه في تبويب جديد.",
      openVideo: "افتح الفيديو في علامة تبويب جديدة",
      play: "تشغيل",
      langLabel: "لغة الفيديو",
      noVideo: "الفيديو التوضيحي قيد الإعداد وسيظهر هنا قريباً.",
      onlyIn: "الفيديو التوضيحي متاح حالياً باللغة {lang} فقط.",
    },
    trust: {
      eyebrow: "الثقة وسجل التدقيق",
      title: "مصمّمة لتُدقَّق.",
      subtitle: "كل قيد يمكن لأي شريك أو مدقق أو جهة تنظيمية التحقق منه — ومقارنته بالسجل الرسمي. وقواعد الميثاق يطبّقها النظام، لا الذاكرة.",
      chips: [
        { k: "charter", label: "مقترحات متوافقة مع الميثاق" },
        { k: "ledger", label: "سجل تدقيق لا يقبل التلاعب" },
        { k: "controls", label: "صلاحيات حسب الدور" },
      ],
      cards: [
        { title: "كل قرار محفوظ بما لا يقبل التلاعب", body: "كل قيد حصة أو قيد نقل أو تصويت أو إشعار أو توزيع مرتبط بالقيد الذي قبله. وأي تعديل لاحق يكسر السلسلة — والمنصة تُخبرك. ويُصدَّر السجل كاملاً لمدققكم." },
        { title: "قواعد ميثاقكم يطبّقها النظام", body: "تُحدَّد قواعد الأغلبية والمواعيد وسياسة التوزيع لكل وعاء وتُطبَّق على كل مقترح وكشف حساب؛ ويُرجَّح كل صوت بالحصة المسجلة ويُكتب مع نتيجته." },
        { title: "سجل — لا صك ولا سعر ولا محفظة", body: "لا تحتفظ «تمرير» بأموال، ولا تحدد سعراً، ولا تنقل ملكية. ويُقيَّد الإتمام فقط بعد تأكيد السجل الرسمي؛ ويسود دائماً السجل الرسمي والترتيبات القانونية بين الأطراف." },
        { title: "كشوف حساب يعتمد عليها مدققكم", body: "تُوزَّع الإيجارات والأرباح والتكاليف المشتركة حسب نسبة الملكية؛ وكشوف حساب الملاك وتصدير سجل التدقيق بنقرة واحدة، بالعربية أو الإنجليزية." },
      ],
      ledgerTitle: "نظرة مباشرة على سجل التدقيق",
      ledgerRows: [
        { kind: "record", tag: "قيد الوعاء", label: "3 أصول · 4 شركاء" },
        { kind: "allocate", tag: "قيد حصة", label: "الفرع أ · 50.00%" },
        { kind: "valuation", tag: "اعتماد التقييم", label: "تحديث قيم الأصول" },
        { kind: "distribution", tag: "توزيع الإيجار", label: "حسب نسبة الملكية" },
        { kind: "transfer", tag: "قيد نقل", label: "مرفق بمرجع السجل" },
        { kind: "retire", tag: "إلغاء قيد حصة", label: "الإتمام مؤكَّد من السجل الرسمي" },
      ],
      verified: "تم التحقق من سلامة السلسلة",
    },
    stats: {
      eyebrow: "مباشر من سجل التدقيق",
      items: [
        { key: "pools_total", label: "أوعية مقيَّدة" },
        { key: "assets_total", label: "أصول مسجّلة" },
        { key: "ledger_transactions", label: "قيود في سجل التدقيق" },
        { key: "owners", label: "شركاء مسجّلون" },
        { key: "total_nav_sar", label: "أصول مقيَّمة تحت الحوكمة", currency: true },
      ],
    },
    faq: {
      eyebrow: "الأسئلة الشائعة",
      title: "ما تسأله المكاتب العائلية والورثة أولاً.",
      items: [
        { q: "هل تملك «تمرير» شيئاً أو تحتفظ به أو تنقله؟", a: "لا. تحتفظ «تمرير» بسجل للملكية المتفق عليها بين الملاك وللقرارات التي يتخذونها. أما الملكية القانونية فيحددها الصك أو سجل الشركة، وهو الذي يسود عند أي اختلاف. ويُتمّ النقل عبر وزارة العدل أو السجلات الرسمية للشركة، وتقيّد «تمرير» الإتمام فقط بعد تأكيد السجل." },
        { q: "كيف يُترجَم ميثاق العائلة إلى قرارات؟", a: "وزن كل شريك هو حصته المسجلة. تحمل المقترحات قاعدة الأغلبية والموعد الذي يشترطه الميثاق أو النظام — الإدارة المعتادة بأغلبية الحصص، والتغييرات الجوهرية بأغلبية أعلى — ويُكتب كل صوت في سجل التدقيق بوزنه ونتيجته." },
        { q: "من يقيّم الأصول، وكم مرة؟", a: "المقيّمون الحاصلون على رخصة «تقييم» سارية والموثّقون من مشرف المنصة فقط، ويتعاقد معهم الملاك مباشرة. يفتح المديرون جولات التقييم وفق الوتيرة التي يحددها الميثاق؛ ويحمل كل تقرير مرجع «قيّم» وملف PDF. وتُعرض القيم لكل أصل، وقيمة كل مالك المنسوبة للاطلاع فقط." },
        { q: "ماذا يحدث عندما يريد شريك نقل حصته؟", a: "يرسل مقترحاً إلى شريك محدد عبر «تمرير»؛ فيقبل ذلك الشريك أو يرفض، ويُبلَّغ الباقون للعلم. وأي سعر يتفق عليه الأطراف وحدهم — فلا تحدده «تمرير» ولا تقترحه ولا تُسوّيه. يُتمّ النقل عبر القناة الرسمية؛ ثم يقيّده المدير بمرجع السجل، فيتحدّث السجل ويمكن لأي من الطرفين الاعتراض إن كان ثمة خطأ." },
        { q: "هل تتعامل «تمرير» بالأموال؟", a: "لا. تُحصَّل الإيجارات والدخل وتُدفع عبر بنك الملاك أو مدير عقاري مرخّص. وتحسب «تمرير» نصيب كل مالك، وتقيّد ما أُعلن وما دُفع، وتُصدر كشوف الحساب. لا أرصدة ولا محافظ على المنصة." },
        { q: "هل «تمرير» سوق أو منتج استثماري؟", a: "لا. لا يُعرض شيء على الجمهور، ولا تُجمع أموال جديدة، ولا يُقبل غرباء، ولا تُسعَّر وحدات أو تُتداول. «تمرير» بنية حوكمة لأصول يملكها الناس معاً أصلاً." },
        { q: "هل «تمرير» متاحة بالعربية؟", a: "نعم. المنصة كاملة، بما فيها الدعوات وكشوف الحساب، متاحة بالعربية بتخطيط من اليمين إلى اليسار، وبالإنجليزية." },
        { q: "ما التقنية التي تحفظ السجل؟", a: "سجل تدقيق داخلي متسلسل بالتجزئة مع تحقق كامل. وهو سجل لحقوق محفوظة في مكان آخر — لا سجل ملكية ولا بلوكتشين عام." },
      ],
    },
    pricing: {
      pageTitle: "الأسعار — تمرير",
      description: "رسوم ثابتة ومعلنة. تدفع المنظمة التي تدير الأوعية؛ والشركاء والورثة والمقيّمون مجاناً دائماً. وليست نسبة من القيمة أبداً.",
      eyebrow: "الأسعار",
      title: "رسوم ثابتة ومعلنة. لا شيء مرتبط بقيمة ما تملكون.",
      subtitle: "المنظمة التي تدير الأوعية — مكتب عائلي أو مكتب محاماة أو مصفّي تركة — تدفع اشتراكاً ثابتاً ورسوماً ثابتة لكل معاملة. أما الشركاء والورثة والمقيّمون فلا يدفعون شيئاً.",
      payerLabel: "من يدفع",
      payer: [
        { k: "org", title: "المنظمة المديرة تدفع", body: "تختار باقة وتدفع الرسوم الثابتة أدناه. اشتراك واحد يغطي جميع أوعيتها ضمن حدود الباقة." },
        { k: "owners", title: "الشركاء والورثة — مجاناً", body: "يدعوهم المدير؛ ويطّلعون على سجلهم ويصوّتون ويستلمون كشوف الحساب دون أي رسوم." },
        { k: "valuers", title: "المقيّمون المعتمدون — مجاناً", body: "يتعاقد معهم الملاك ويدفعون لهم مباشرة. ولا تأخذ «تمرير» عمولة إحالة ولا حصة من عملهم." },
      ],
      plansLabel: "الباقات",
      plansTitle: "ثلاث باقات. أسعار ثابتة.",
      plansIntro: "اختر الباقة بحسب عدد الأوعية والملاك الذين تديرهم منظمتك. ويمكن التبديل أو الإلغاء في نهاية الفترة.",
      billing: { monthly: "شهرياً", yearly: "سنوياً", quotedLabel: "بعرض سعر لكل عائلة", quotedRange: "عادةً بين {from} و{to} سنوياً", exVat: "غير شامل الضريبة" },
      choose: "أنشئ حسابك",
      quote: "اطلب عرض سعر",
      perMatterLabel: "رسوم ثابتة لكل معاملة",
      perMatterTitle: "رسم ثابت واحد لكل معاملة مقيَّدة.",
      perMatterIntro: "تُحتسب على المنظمة المديرة مرة واحدة لكل معاملة مقيَّدة، لحظة موافقة المدير عليها — مبلغ ثابت دائماً، وليس حصة من القيمة المعنية أبداً.",
      perMatterCols: { matter: "المعاملة", fee: "الرسم" },
      vat: "جميع المبالغ غير شاملة ضريبة القيمة المضافة بنسبة {vat}%.",
      effective: "يسري الجدول اعتباراً من {date}؛ ويُعلَن عن أي تغيير قبل {days} يوماً.",
      indicative: "جدول استرشادي — يُعتمد قبل الإطلاق.",
      notesLabel: "من المفيد معرفته",
      notes: [
        "تُصدر الفواتير باسم المنظمة وتُسدَّد بالتحويل البنكي. لا يُحصَّل أي مبلغ عبر المنصة ولا تمرّ أموال الملاك عبرها أبداً.",
        "لا رسم أبداً كنسبة من قيمة أصل أو من سعر نقل أو من توزيع.",
        "كل إجراء يُنشئ رسماً يعرض المبلغ الدقيق أولاً؛ ويوافق المدير عليه قبل كتابة القيد.",
        "الإفصاح عن الرسوم هو النسخة الملزِمة من هذا الجدول.",
      ],
      faqLabel: "أسئلة حول الأسعار",
      faq: [
        { q: "لماذا ليست نسبة من القيمة؟", a: "لأن «تمرير» لا تتوسّط ولا تحتفظ ولا تبيع شيئاً. والنسبة تربط رسومنا بقيمة ما تملكون؛ أما الرسوم الثابتة فتُبقي المنصة مستقلة عن القرارات المقيَّدة فيها." },
        { q: "ماذا أدفع قبل تفعيل أول وعاء؟", a: "لا شيء. إنشاء الحساب ودعوة فريقك ونمذجة وعاء كلها مجانية. وينشأ أول رسم عند تفعيل وعاء وموافقة المدير على رسم الإعداد." },
        { q: "هل تغطي باقة واحدة عدة عائلات؟", a: "باقتا «الأساسية» و«الاحترافية» للمنظمات التي تدير أوعية لصالح الغير، وتُحتسب فيهما الأوعية النشطة والملاك عبر الجميع. أما «المكتب العائلي» فلأوعية عائلة واحدة، بعرض سعر بحسب حجمها." },
      ],
      ctaTitle: "مستعد لتحديد الرقم لعائلتك أو مكتبك؟",
      ctaBody: "أنشئ حسابك للبدء، أو راسلنا للحصول على عرض سعر للمكتب العائلي.",
      cta: "أنشئ حسابك",
      contact: "تواصل معنا",
      disclosure: "اقرأ الإفصاح الكامل عن الرسوم",
      loadError: "تعذّر تحميل الجدول المنشور. وتحمل صفحة الإفصاح عن الرسوم النسخة المعتمدة.",
    },
    closing: {
      title: "مستعد لإدارة أصولكم المشتركة كمؤسسة؟",
      body: "أنشئ وعاءً، وادعُ الشركاء، ودع سجلاً واحداً متفقاً عليه يحفظ نزاهة الحوكمة.",
      cta: "أنشئ حسابك",
      secondary: "نمذجة وعاء أولاً",
      reducers: ["مجاني للشركاء والمقيّمين", "رسوم ثابتة ومعلنة", "لا تسعير ولا تداول"],
    },
    footer: {
      note: "تمرير — حوكمة الأصول المشتركة · الرياض، المملكة العربية السعودية",
      legal: "سجل لملكية متفق عليها، لا سجل ملكية رسمي — والصك أو سجل الشركة هو الذي يسود.",
      software: "«تمرير» برمجية للسجلات والحوكمة. لا تحتفظ بأموال، ولا تسعّر الحصص أو تتداولها، ولا تنقل ملكية. وبيانات المشغّل مبيّنة في شروط الاستخدام.",
      productHeading: "المنتج",
      legalHeading: "القانوني",
      contactHeading: "التواصل",
      complaints: "الشكاوى",
      complaintsEmail: "complaints@tamreer.net",
      complaintsNote: "إقرار بالاستلام خلال يومي عمل، وردّ خلال 15 يوم عمل.",
      languageLabel: "English",
      languageHint: "اقرأ هذه الصفحة بالإنجليزية",
      productLinks: [
        { id: "personas", label: "لمن هي", href: "#personas" },
        { id: "how", label: "كيف تعمل", href: "#how" },
        { id: "screens", label: "المنتج", href: "#screens" },
        { id: "calculator", label: "نمذجة وعاء", to: "/model" },
        { id: "pricing", label: "الأسعار", to: "/pricing" },
        { id: "trust", label: "الثقة وسجل التدقيق", href: "#trust" },
        { id: "faq", label: "الأسئلة الشائعة", href: "#faq" },
      ],
      links: [
        { id: "terms", label: "شروط الاستخدام", to: "/legal/terms" },
        { id: "privacy", label: "إشعار الخصوصية", to: "/legal/privacy" },
        { id: "fees", label: "الرسوم", to: "/legal/fees" },
        { id: "contact", label: "التواصل والشكاوى", to: "/legal/contact" },
      ],
    },
  },
};
